import { Message } from '../../../types/models';
import { formatMessages, formatStreamingContent } from './messageFormatter';
import { QwenChatRequest, QwenChatResponse } from './types';
import { getProviderConfig, getProviderUrl, getProviderEndpoint } from '../../../config/modelsDatabase';

export class QwenClient {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async chat(messages: Message[], modelId: string): Promise<string> {
    console.log('Qwen chat request:', { modelId, messageCount: messages.length });

    // Get Qwen configuration
    const config = getProviderConfig('qwen');
    if (!config) {
      throw new Error('Qwen provider configuration not found');
    }

    const chatEndpoint = getProviderEndpoint('qwen', 'chat');
    if (!chatEndpoint) {
      throw new Error('Qwen chat endpoint not found');
    }

    const formattedMessages = formatMessages(messages);

    const requestBody: QwenChatRequest = {
      model: modelId,
      messages: formattedMessages,
      stream: false,
      temperature: 0.7,
      top_p: 0.95,
      max_tokens: 4096,
      presence_penalty: 0,
      frequency_penalty: 0
    };
    
    console.log('Qwen API request body:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(getProviderUrl('qwen', chatEndpoint), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
        ...config.headers // Include any provider-specific headers
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMessage = `Qwen API Error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('Qwen API Error Response:', errorData);
        if (errorData.error) {
          errorMessage = typeof errorData.error === 'string'
            ? errorData.error
            : errorData.error.message || errorMessage;
        }
      } catch (parseError) {
        console.error('Failed to parse Qwen error response:', parseError);
        // Try to get the raw text if JSON parsing fails
        const rawText = await response.text().catch(() => '');
        if (rawText) {
          console.error('Raw error response:', rawText);
          errorMessage = rawText;
        }
      }

      // Handle specific error cases
      if (response.status === 401) {
        throw new Error('Invalid API key. Please check your Qwen API key.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status === 404) {
        throw new Error('Invalid model ID or endpoint not found.');
      }

      throw new Error(errorMessage);
    }

    const data = await response.json() as QwenChatResponse;
    console.log('Qwen API response:', data);

    if (!data.choices?.[0]?.message?.content) {
      console.error('Unexpected Qwen API response structure:', data);
      throw new Error('Invalid response format from Qwen API');
    }

    return data.choices[0].message.content;
  }
}